<script setup lang="ts">
import VarsityContentProcessor from "@/components/VarsityContentProcessor.vue"
import { useMediaUrl, useVarsityAppConfig } from "@/composables"
import type { Organization } from "@/model/app/organization"
import type { CmsPagePart } from "@/model/c-m-s/cms-page-part"
import type { Product } from "@/model/order/product"
import type { Issue } from "@/model/tasks/issue"
import { useHttpClient } from "@/vf"
import { useRouter } from "vue-router"

const props = defineProps<{
    element: CmsPagePart & { config: { count: number } }
}>()
const http = useHttpClient()
const router = useRouter()
const { appConfig } = useVarsityAppConfig()
const { uploadCdnUrl } = useMediaUrl()

const products = (
    await http.get<{
        list: Product[]
    }>("shop/catalog/products", {
        itemsPerPage: "-1",
        products: props.element.config.products,
        shopConfiguration: props.element.config.shopConfiguration,
    })
).data.list
// All products of the element belong to the same shop configuration
const shopConfiguration = products[0]?.shopConfiguration

const organizations: Organization[] = appConfig.value.login
    ? (
          await http.get<{ id: string; name: string; level: string }[]>(
              "organization/by-login/" + appConfig.value.login.id + "/organizations/accessible",
          )
      ).data
    : []

const allowedBuyerTypes = shopConfiguration?.allowedBuyerTypes ?? []
const canBuyAsUser = allowedBuyerTypes.length === 0 || allowedBuyerTypes.includes("User")
const canBuyAsOrganization = allowedBuyerTypes.length === 0 || allowedBuyerTypes.includes("Organization")

// Some shop areas (e.g. Workshops & Camps are booked rather than ordered) use button wording of their own
const startOrderKey = `@shop:widget.action.start_order.${shopConfiguration?.textVariant}`

const championship = shopConfiguration?.championship
const registeredOrganizationIds: string[] =
    championship && appConfig.value.login
        ? (
              await http.get<{ organizationIds: string[] }>(
                  `order/shop/${props.element.config.shopConfiguration}/registered-organizations`,
              )
          ).data.organizationIds
        : []

const eligibleOrganizations = championship
    ? organizations.filter(org => registeredOrganizationIds.includes(org.id!))
    : organizations

async function startNewOrder(organization: Organization | null = null) {
    const issue = (
        await http.post<Issue>(`/order/shop/${props.element.config.shopConfiguration}/new-order/${organization?.id}`)
    ).data
    await router.push({ name: "@Tasks.Customer.index", query: { issue: issue.id } })
}
</script>

<template>
    <div class="product-list row my-md-5">
        <div class="col-md-8">
            <template v-for="product of products" :key="product.id">
                <div class="row">
                    <div class="col-xl-6">
                        <div class="px-3 px-lg-5">
                            <img
                                :src="uploadCdnUrl(product.galleryImage?.filename!)"
                                :alt="product.galleryImage?.altText"
                                v-if="product.galleryImage?.filename"
                                class="img-fluid"
                            />
                        </div>
                    </div>
                    <div class="col-xl-6">
                        <div class="px-3 px-lg-5 py-4 py-xl-0">
                            <h2>{{ product.name }}</h2>
                            <VarsityContentProcessor :content="product.description" />
                        </div>
                    </div>
                </div>
            </template>
        </div>
        <div
            class="product-list__order-buttons col-md-4 py-4 py-md-0 order-first order-md-0"
            v-if="products.some(product => product.isPurchasable)"
        >
            <div class="px-3 px-lg-5">
                <div v-if="shopConfiguration?.orderNote" class="alert alert-gray mb-5">
                    <VarsityContentProcessor :content="shopConfiguration.orderNote" />
                </div>
                <template v-if="canBuyAsUser && appConfig.login">
                    <h2 class="mb-4">{{ $t(`${startOrderKey}.for_me.title`) }}</h2>
                    <div class="mb-4">
                        <button class="btn btn-primary" @click="startNewOrder()">
                            {{ $t(`${startOrderKey}.for_me`) }}
                        </button>
                    </div>
                </template>
                <template v-if="canBuyAsOrganization && eligibleOrganizations.length > 0">
                    <h2 class="mb-4">{{ $t(`${startOrderKey}.for_organization.title`) }}</h2>
                    <template v-for="organization of eligibleOrganizations" :key="organization.id">
                        <div class="mb-4">
                            <button class="btn btn-primary" @click="startNewOrder(organization)">
                                {{
                                    $t(`${startOrderKey}.for_organization`, {
                                        organizationName: organization.name,
                                    })
                                }}
                            </button>
                        </div>
                    </template>
                </template>
                <template
                    v-if="
                        championship &&
                        canBuyAsOrganization &&
                        organizations.length > 0 &&
                        eligibleOrganizations.length === 0
                    "
                >
                    <div class="alert alert-warning">
                        {{ $t("@shop:widget.championship_not_registered") }}
                    </div>
                </template>
                <template v-if="!appConfig.login">
                    <div class="alert alert-primary">
                        {{ $t("@shop:widget.action.start_order.hint_login") }}
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@import "@/styles/variables";

.product-list {
    &__order-buttons {
        @include media-breakpoint-up(md) {
            border-left: 2px solid $gray;
        }
    }
}
</style>
